/**
 * ADR-0235 trust chip on the visits panel: the Agents page's `TrustChip`
 * ("Why?" + "This is wrong"), shown on agent rows and in the detail
 * header. Renders nothing unless the site's verdicts are live (the
 * provider holds `null` then) and `visitTrustVerdict` allows a chip; see
 * `visit-trust.ts` for the precedence with Mismatch and Probing.
 */

import { createContext, useContext, type ReactNode } from 'react';

import type { AuditRecord } from '@sill/types';
import type { MerchantAgentDisputeRef, MerchantAgentVerdict } from '@sill/types/api';

import { TrustChip } from '../agents/trust/TrustChip.js';
import { visitTrustVerdict, type VisitTrustSurface } from './visit-trust.js';

export interface VisitTrustValue {
  readonly verdicts: readonly MerchantAgentVerdict[];
  readonly site_id: string;
  readonly canDispute: boolean;
  /** "last 30 days" — the verdict window, for the popover footer. */
  readonly windowPhrase: string;
  readonly onDisputed: (claim_key: string, dispute: MerchantAgentDisputeRef) => void;
}

const Ctx = createContext<VisitTrustValue | null>(null);

/** `value` is null while verdicts are off for the site: every chip renders nothing. */
export function VisitTrustProvider({ value, children }: { readonly value: VisitTrustValue | null; readonly children: ReactNode }): JSX.Element {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function VisitTrustChip({
  record,
  name,
  surface,
  mismatch = false,
  probing = false,
}: {
  readonly record: AuditRecord;
  /** The name the row / header shows ("GPTBot"). */
  readonly name: string;
  readonly surface: VisitTrustSurface;
  readonly mismatch?: boolean;
  readonly probing?: boolean;
}): JSX.Element | null {
  const trust = useContext(Ctx);
  if (trust === null) return null;
  const verdict = visitTrustVerdict(record, trust.verdicts, { surface, mismatch, probing });
  if (verdict === null) return null;
  return (
    <span data-testid={`visit-trust-${surface}`} data-trust-label={verdict.label} className="inline-flex items-center">
      <TrustChip
        verdict={verdict}
        name={name}
        site_id={trust.site_id}
        canDispute={trust.canDispute}
        windowPhrase={trust.windowPhrase}
        onDisputed={trust.onDisputed}
        compact={surface === 'row'}
      />
    </span>
  );
}
